// ProcessComponent.tsx
import React, { useState, useEffect, RefObject, useMemo } from "react";
import "./index.less";
import { elementDesc } from "@/config/dataMap";
import TowerPage from "./ElementPages/Tower";
import MixerPage from "./ElementPages/Mixer";
import { Button, Dropdown, Checkbox, Spin, Select } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { designValue, getDesignValue, actList, getActValue } from "@/services/design";
import { jobs, getJob, getSubJobs,getSubJob } from "@/services/common";
import { useParams } from "react-router-dom";
import userInfo from "@/Components/user";
import dayjs from "dayjs";
import { useCompareData } from "./ResultPageFrame";
import materialsDB from "@/constants/materialsDB";
import { rebuildFormula } from "@/config/func";

// 引擎返回的 JSON 文本可能包含非法 NaN（与结果抽屉解析一致），解析前统一替换为 0，避免 JSON.parse 崩溃
const jsonParseSafe = (str: any, fallback: any = {}) => {
  if (typeof str !== "string") return str ?? fallback;
  try {
    return JSON.parse(str.replace(/NaN/g, "0"));
  } catch (e) {
    try {
      return JSON.parse(str);
    } catch (e2) {
      return fallback;
    }
  }
};

// 参考 DesignValue 的摩尔流量换算：为设计值流股计算 f_mol_per_h
const enrichDesignValueMolarFlow = (data: any) => {
  if (!data?.values?.mix_input?.streams) return data;
  const casToMW: Record<string, number> = {};
  materialsDB.forEach((mat: any) => {
    if (mat.cas) casToMW[mat.cas] = mat.MW;
  });
  const enriched = { ...data };
  enriched.values = { ...data.values };
  enriched.values.mix_input = { ...data.values.mix_input };
  enriched.values.mix_input.streams = data.values.mix_input.streams.map((s: any) => {
    if (s.f_mol_per_h && s.f_mol_per_h > 0) return s; // 已有有效值则跳过
    const mole_frac_arr = s.materials?.map((m: any) => m.molar_frac || 0) || [];
    const cas_arr = s.materials?.map((m: any) => m.cas) || [];
    let avgMolarMass = 0;
    mole_frac_arr.forEach((mf: number, idx: number) => {
      const mw = casToMW[cas_arr[idx]] || 0;
      avgMolarMass += (mw / 1000) * mf;
    });
    const mass_flow = s.params?.f || 0;
    const f_mol_per_h = avgMolarMass > 0 ? mass_flow / avgMolarMass : 0;
    return { ...s, f_mol_per_h };
  });
  return enriched;
};


interface ProcessComponentProps {
    input: any;
    transInput: any;
    results: any;
    selectTreeButton: any;
    scrollViewRef: RefObject<HTMLDivElement>;
    job_id: number;
    type: string;
    subJobid: number;
    item_name: string;
    streamDescMap?: Record<string, string>;
    streamNodeMap?: Record<string, { fromName: string; toName: string }>;
    elementDescMap?: Record<string, string>;
    tempUnit?: string;
    pressUnit?: string;
    setTempUnit?: (v: string) => void;
    setPressUnit?: (v: string) => void;
}

const towerResultPages = ["工艺计算结果", "水力学计算"];
const commonResultPages = ["流股工艺数据", "流股组分数据"];

const ProcessComponent = ({
    subJobid,
    item_name,
    input,
    transInput,
    results,
    selectTreeButton,
    scrollViewRef,
    job_id,
    type,
    streamDescMap,
    streamNodeMap,
    elementDescMap,
    tempUnit,
    pressUnit,
    setTempUnit,
    setPressUnit,
}: ProcessComponentProps) => {
    // 填料塔没有塔板，不显示"水力学计算"页签
    const getTowerResultPages = () => {
        if (selectTreeButton?.type === "DistilStrict") {
            const el = input?.inputs?.elements?.find(
                (item: any) => item?.name === selectTreeButton?.name
            );
            if (el?.params?.internalType === "填料") {
                return ["工艺计算结果"];
            }
        }
        return towerResultPages;
    };

    const [selectResultPage, setSelectResultPage] = useState(getTowerResultPages()[0]);
    const [elementTabs, setElementTabs] = useState<any>(getTowerResultPages());
    const params = useParams<{ k: string; f: string; j: string }>();

    // ===================== 对比下拉多选 =====================
    const [dataList, setDataList] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [filterType, setFilterType] = useState<"all" | "design" | "actual" | "task" | "otherCalc">("all");
    // 对比数据由父组件 Context 统一管理（与流股/输入参数同步）
    const { compareItems, setCompareItems } = useCompareData(); // 保存选中的对比数据
    // 对比项变更时同步下拉勾选状态（三处页面共享同一份对比状态）
    useEffect(() => {
        setSelectedIds(compareItems.map((item: any) => item.id));
    }, [compareItems]);
    const [dropdownOpen, setDropdownOpen] = useState(false); // 控制下拉菜单打开/关闭
    const [taskDataList, setTaskDataList] = useState<any[]>([]); // 任务数据列表
    const [confirmLoading, setConfirmLoading] = useState(false); // 确认选择时的 loading 状态
    const [otherCalcResults, setOtherCalcResults] = useState<any[]>([]); // 当前元件其他计算结果
    const [otherCalcLoading, setOtherCalcLoading] = useState(false); // 加载其他计算结果的状态
    // 实际值存储列表（actList 接口获取，[{ id, name }]）
    const [actListData, setActListData] = useState<any[]>([]);

    // 运行数据的 molar_frac 是对象数组 [{ "H₂S": 0.000003 }, ...]，且不一定覆盖所有组分；
    // 按输入组分顺序映射为 materials 数组，没有对应值的组分摩尔分率为 null（显示为 "-"）
    const buildTimeRangeMaterials = (s: any) => {
        const refStream = (input?.inputs?.streams || []).find((item: any) => item?.materials?.length);
        const inputMats = refStream?.materials || [];
        const fracMap: Record<string, number> = {};
        (s.molar_frac || []).forEach((item: any) => {
            if (item && typeof item === "object") {
                Object.entries(item).forEach(([name, v]) => {
                    if (v != null) fracMap[name] = Number(v);
                });
            }
        });
        const result = inputMats.map((mat: any) => {
            const key = mat?.name ? rebuildFormula(String(mat?.name)) : "";
            const v = fracMap[key] ?? fracMap[mat?.name];
            return { molar_frac: v ?? null };
        });
        console.log("[buildTimeRangeMaterials] 流股:", s?.name, "| 输入组分:", inputMats.map((m: any) => m?.name), "| 原始 molar_frac:", s.molar_frac, "| 映射结果:", result);
        return result;
    };

    // 获取当前元件其他计算结果
    const fetchOtherCalcResults = async () => {
        if (type !== 'subJob') return;
        setOtherCalcLoading(true);
        try {
            const res = await getSubJobs(
                {
                    job_id: subJobid,
                    name: item_name,
                },
                userInfo.access_token,
            );
            if (res.status === 200) {
                // 过滤掉当前任务，并且过滤掉计算失败或未计算的结果
                console.log('res.data111:', res.data);
                const filteredResults = res.data.filter((item: any) => 
                    String(item.id) !== String(params.j || job_id) && item.status === "done"
                    && item.name===item_name
                );
                setOtherCalcResults(filteredResults);
            }
        } catch (err) {
            console.error("获取其他计算结果失败", err);
        } finally {
            setOtherCalcLoading(false);
        }
    };

    // 获取当前组合计算的其他运行结果（type=multiJobs：同组合名多次运行中，除当前运行外的其他次）
    const fetchComboOtherCalcResults = async () => {
        if (type !== 'multiJobs') return;
        setOtherCalcLoading(true);
        try {
            const res = await getSubJobs({ job_id: subJobid }, userInfo.access_token);
            if (res.status === 200) {
                const list = res.data || [];
                // 当前运行所在组合名：以当前 subjob(id=job_id) 的行记录为准
                const cur = list.find(
                    (item: any) => String(item.id) === String(job_id) && item.type === 'multi',
                );
                if (!cur) {
                    setOtherCalcResults([]);
                    return;
                }
                const filteredResults = list.filter(
                    (item: any) =>
                        item.type === 'multi' &&
                        item.name === cur.name &&
                        String(item.id) !== String(job_id) &&
                        item.status === 'done',
                );
                setOtherCalcResults(filteredResults);
            }
        } catch (err) {
            console.error("获取组合计算其他运行结果失败", err);
        } finally {
            setOtherCalcLoading(false);
        }
    };

    // 获取设计值/实际值数据
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await designValue({ flow_id: params.f }, userInfo.access_token);
            setDataList(res.data || []);
            console.log(res.data);
        } catch (err) {
            console.error("获取失败", err);
        } finally {
            setLoading(false);
        }
    };

    // 获取任务数据
    const fetchTaskData = async () => {
        setLoading(true);
        try {
            const res = await jobs({ flow_id: params.f, page: 1, perPage: 1000 }, userInfo.access_token);
            const allTaskData = res.data.data || [];
            const filteredData = params.j || subJobid
                ? allTaskData.filter((item: any) => String(item.id) !== String(params.j || subJobid))
                : allTaskData;
            // 过滤掉计算失败或未计算的任务（status 不是 "done" 的排除）
            const successData = filteredData.filter((item: any) => item.status === "done");
            setTaskDataList(successData);
        } catch (err) {
            console.error("获取任务数据失败", err);
        } finally {
            setLoading(false);
        }
    };

    // 获取实际值存储列表
    const fetchActList = async () => {
        try {
            const res = await actList(userInfo.access_token);
            // 返回结构为 { records: [{ id, name }] }
            setActListData(res.data?.records || []);
            console.log("实际值存储列表:", res.data);
        } catch (err) {
            console.error("获取实际值存储列表失败", err);
            setActListData([]);
        }
    };

    // 下拉打开时获取数据
    const handleDropdownOpen = (open: boolean) => {
        setDropdownOpen(open);
        if (open) {
            fetchData();
            fetchTaskData();
            fetchActList();
        }
    };
    // const filteredList = dataList.filter((item) => {
    //     if (filterType === "all") return true;
    //     return item.type === filterType;
    // });

    // 筛选（包括设计值、实际值存储项、任务数据和其他计算结果）
    const filteredList = useMemo(() => {
        // 实际值存储项：actList 接口返回的存储工况记录
        const actItems = actListData.map((rec) => ({
            id: `act-${rec.id}`,
            storeId: rec.id,
            name: rec.name,
            source: 'act',
        }));
        if (filterType === "all") {
            // 全部：显示设计值 + 实际值 + 任务数据
            return [
                ...dataList.map(item => ({ ...item, source: 'design' })),
                ...actItems,
                ...taskDataList.map(item => ({ ...item, source: 'task' })),
                ...otherCalcResults.map(item => ({ ...item, source: 'otherCalc' })),
            ];
        } else if (filterType === "design") {
            return dataList.filter(item => item.type === "design").map(item => ({ ...item, source: 'design' }));
        } else if (filterType === "actual") {
            return actItems;
        } else if (filterType === "task") {
            return taskDataList.map(item => ({ ...item, source: 'task' }));
        } else if (filterType === "otherCalc") {
            // 当前元件其他计算结果
            return otherCalcResults.map(item => ({ ...item, source: 'otherCalc' }));
        }
        return [];
    }, [dataList, taskDataList, filterType, otherCalcResults, actListData]);
    console.log("filteredList:", filteredList);

    // 多选切换
    const toggleSelect = (id: string) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    // 确认选择
    // const handleConfirm = () => {
    //     console.log(selectedIds);
    //     const selectedItems = dataList.filter((item) => selectedIds.includes(item.id));
    //     console.log("✅ 选中的对比数据：", selectedItems);
    //     setCompareItems(selectedItems); // 保存到状态，传给子组件
    //     setDropdownOpen(false); // 关闭下拉菜单
    // };
    const handleConfirm = async () => {
        setConfirmLoading(true); // 开始 loading
        try {
            const fetchPromises = selectedIds.map(async (id) => {
                try {
                    // 在所有数据源中查找对应的项目，判断来源（不依赖 filteredList）
                    const taskItem = taskDataList.find(item => String(item.id) === String(id));
                    const otherCalcItem = otherCalcResults.find(item => String(item.id) === String(id));
                    const actRec = actListData.find(r => `act-${r.id}` === id);

                    if (actRec) {
                        // 实际值存储项：通过 getActValue 获取存储的实际值数据
                        const res = await getActValue(actRec.id, userInfo.access_token);
                        const actData = res.data.result; // 详情数据（结构同 realtime 返回的 { input, output }）
                        console.log("实际值存储详情:", actData);
                        const outputData = actData.output;
                        const inputData = actData.input;

                        // 与任务数据一致，直接用输出数据构建对比格式
                        const convertedStreams = (outputData.streams || []).map((s: any) => ({
                          name: s.name,
                          params: {
                            f: s.f_kg_per_h,
                            f_unit: "kg/h",
                            t: s.T_C,
                            t_unit: "C",
                            p: s.P_MPa,
                            p_unit: "MPa",
                          },
                          materials: buildTimeRangeMaterials(s),
                          vf: s.vf,
                          H: s.H,
                          mass_frac: s.mass_frac,
                            f_mol_per_h: s.f_mol_per_h,
                            f_nm3_per_h: s.f_nm3_per_h,
                            f_m3_per_h: s.f_m3_per_h,
                            f_l_per_h: s.f_l_per_h,
                        }));

                        return {
                          id,
                          values: {
                            name: actRec.name,
                            mix_input: { streams: convertedStreams, elements: outputData.elements || [] },
                            fullInput: inputData, // 完整输入数据，供输入参数页对比展示
                            rawInput: inputData, // 接口返回的原始 input
                            rawOutput: outputData, // 接口返回的原始 output
                          },
                          type: "actual",
                          name: actRec.name,
                        };
                    } else if (taskItem) {
                        // 任务数据：使用 getJob 接口
                        const res = await getJob(id, userInfo.access_token);
                        console.log("对比任务数据原始响应:", jsonParseSafe(res.data.input, null), jsonParseSafe(res.data.output, null));

                        const outputData = jsonParseSafe(res.data.output);
                        const inputData = jsonParseSafe(res.data.input);

                        // 直接用输出数据构建对比格式，与当前计算结果保持一致
                        const convertedStreams = (outputData.streams || []).map((s: any) => ({
                          name: s.name,
                          params: {
                            f: s.f_kg_per_h ?? 0,
                            f_unit: "kg/h",
                            t: s.T_C ?? 0,
                            t_unit: "C",
                            p: s.P_MPa ?? 0,
                            p_unit: "MPa",
                          },
                          materials: (s.molar_frac || []).map((mf: number) => ({
                            molar_frac: mf ?? 0,
                          })),
                          vf: s.vf,
                          H: s.H,
                          mass_frac: s.mass_frac,
                            f_mol_per_h: s.f_mol_per_h,
                            f_nm3_per_h: s.f_nm3_per_h,
                            f_m3_per_h: s.f_m3_per_h,
                            f_l_per_h: s.f_l_per_h,
                        }));

                        const data = {
                          id: res.data.id,
                          values: {
                            name: res.data.name,
                            mix_input: { streams: convertedStreams, elements: outputData.elements || [] },
                            fullInput: inputData, // 完整输入数据，供输入参数页对比展示
                          },
                          type: "task",
                        };
                        return data;
                    } else if (otherCalcItem) {
                        // 当前元件其他计算结果：使用 getSubJob 获取完整数据
                        const res = await getSubJob(id, userInfo.access_token);
                        // 过滤掉计算失败或未计算的任务
                        if (res.data.status !== "done") return null;
                        const outputData = jsonParseSafe(res.data.output);
                        const inputData = jsonParseSafe(res.data.input);

                        // 直接用输出数据构建对比格式，与当前计算结果保持一致
                        const convertedStreams = (outputData.streams || []).map((s: any) => ({
                          name: s.name,
                          params: {
                            f: s.f_kg_per_h ?? 0,
                            f_unit: "kg/h",
                            t: s.T_C ?? 0,
                            t_unit: "C",
                            p: s.P_MPa ?? 0,
                            p_unit: "MPa",
                          },
                          materials: (s.molar_frac || []).map((mf: number) => ({
                            molar_frac: mf ?? 0,
                          })),
                          vf: s.vf,
                          H: s.H,
                          mass_frac: s.mass_frac,
                            f_mol_per_h: s.f_mol_per_h,
                            f_nm3_per_h: s.f_nm3_per_h,
                            f_m3_per_h: s.f_m3_per_h,
                            f_l_per_h: s.f_l_per_h,
                        }));

                        const data = {
                          id: res.data.id,
                          values: {
                            name: res.data.name,
                            mix_input: { streams: convertedStreams, elements: outputData.elements || [] },
                            fullInput: inputData, // 完整输入数据，供输入参数页对比展示
                          },
                          created_at: res.data.created_at,
                          type: "otherCalc",
                        };
                        return data;
                    } else {
                        // 设计值/实际值：使用 getDesignValue 接口
                        const res = await getDesignValue(id, userInfo.access_token);
                        console.log("设计值/实际值:", res.data);
                        return enrichDesignValueMolarFlow(res.data);
                    }
                } catch (err) {
                    console.error("获取失败", id, err);
                    return null;
                }
            });
            // 2. 等待所有请求完成
            let selectedItems = await Promise.all(fetchPromises);
            // 3. 过滤掉失败的
            selectedItems = selectedItems.filter(Boolean);
            console.log("选中的对比项数据集合:", selectedItems);
            setCompareItems(selectedItems);
            setDropdownOpen(false);
        } finally {
            setConfirmLoading(false); // 结束 loading
        }
    };

    // ===================== 设备逻辑 =====================
    useEffect(() => {
        if (selectTreeButton.type === "DistilStrict") {
            const pages = getTowerResultPages();
            setElementTabs(pages);
            setSelectResultPage(pages[0]);
        } else {
            // setElementTabs(commonResultPages);
            // setSelectResultPage(commonResultPages[0]);
            // 非塔元件不再做页签切换，直接展示流股工艺数据 + 流股组分数据
            setElementTabs([]);
        }
        // 切换设备时，清除之前的其他计算结果，确保下次点击按钮时重新获取
        setOtherCalcResults([]);
        if (type === 'subJob') {
            fetchOtherCalcResults();
        } else if (type === 'multiJobs') {
            fetchComboOtherCalcResults();
        }
    }, [selectTreeButton]);

    const InOutStreams = useMemo(() => {
        if (!input?.inputs?.elements) return {};
        const result: Record<string, { in: string[]; out: string[] }> = {};
        input.inputs.elements.forEach((unit: any) => {
            const unitName = unit.name;
            if (!unitName) return;
            const inStreams = unit.inputStreams?.map((stream: any) => stream.name) || [];
            const outStreams = unit.outputStreams?.map((stream: any) => stream.name) || [];
            result[unitName] = { in: inStreams, out: outStreams };
        });
        return result;
    }, [input]);

    const buildSource = (elementName: string) => {
        if (!results) return { inputStreams: [], outStreams: [] };
        const { streams } = results;
        const s = InOutStreams[elementName] || { in: [], out: [] };
        const inputStreams = s.in.map((i: string) => streams?.find((s: any) => s.name === i) || {});
        const outStreams = s.out.map((i: string) => streams?.find((s: any) => s.name === i) || {});
        return { inputStreams, outStreams };
    };

    const showElementPage = () => {
        if (selectTreeButton.type === "DistilStrict") {
            return (
                <TowerPage
                    input={input}
                    transInput={transInput}
                    results={results}
                    selectTreeButton={selectTreeButton.name}
                    scrollViewRef={scrollViewRef}
                    selectResultPage={selectResultPage}
                    compareItems={compareItems}
                    streamDescMap={streamDescMap}
                    streamNodeMap={streamNodeMap}
                    elementDescMap={elementDescMap}
                    tempUnit={tempUnit}
                    pressUnit={pressUnit}
                />
            );
        } else {
            return (          
                <MixerPage
                    input={input}
                    transInput={transInput}
                    results={buildSource(selectTreeButton.name)}
                    selectTreeButton={selectTreeButton.name}
                    selectTreeButtonType={selectTreeButton.type}
                    selectResultPage={selectResultPage}
                    compareItems={compareItems} // 传递对比数据
                    streamDescMap={streamDescMap}
                    streamNodeMap={streamNodeMap}
                    elementDescMap={elementDescMap}
                    tempUnit={tempUnit}
                    pressUnit={pressUnit}
                />
            );
        }
    };

    // ===================== 下拉面板内容 =====================
    const dropdownContent = (
        <div style={{
            width: type === 'subJob' ? 470 : 320,
            padding: 12,
            // maxHeight: 400,
            // overflow: "auto",
            boxSizing: "border-box",
            backgroundColor: "#f0efefff",
        }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                <Button size="small" type={filterType === "all" ? "primary" : "default"} onClick={() => setFilterType("all")}>全部</Button>
                <Button size="small" type={filterType === "design" ? "primary" : "default"} onClick={() => setFilterType("design")}>设计值</Button>
                <Button size="small" type={filterType === "actual" ? "primary" : "default"} onClick={() => setFilterType("actual")}>运行数据 
                    {/* <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, marginLeft: 4, cursor: "pointer", background: "#d9d9d9", color: "#000", fontSize: 11, fontWeight: "bold", lineHeight: 1 }} onClick={(e) => { e.stopPropagation(); setTimeRangeOpen(true); }}>+</span> */}
                </Button>
                <Button size="small" type={filterType === "task" ? "primary" : "default"} onClick={() => setFilterType("task")}>其他任务</Button>
                {type === 'subJob' && (
                    <Button 
                        size="small" 
                        type={filterType === "otherCalc" ? "primary" : "default"} 
                        onClick={() => {
                            setFilterType("otherCalc");
                            fetchOtherCalcResults();
                        }}
                    >
                        当前元件其他任务
                    </Button>
                )}
                {type === 'multiJobs' && (
                    <Button
                        size="small"
                        type={filterType === "otherCalc" ? "primary" : "default"}
                        onClick={() => {
                            setFilterType("otherCalc");
                            fetchComboOtherCalcResults();
                        }}
                    >
                        当前组合计算其他任务
                    </Button>
                )}
            </div>

            <div style={{maxHeight:350,overflow: "auto"}}>
                {loading ? (
                    <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
                ) : filteredList.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#999" }}>暂无数据</div>
                ) : (
                    filteredList.map((item) => (
                        <div
                            key={item.id}
                            style={{
                                padding: "8px 10px",
                                border: "1px solid #eee",
                                borderRadius: 4,
                                marginBottom: 6,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <span>
                                {/* {item.type === "design" ? "设计值" : "实际值"} {item.id} */}
                                {item.source === 'task' 
                                    ?  `${item.name || item.id}`
                                    : item.source === 'otherCalc'
                                    ? `${item.name || item.id} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
                                    : item.source === 'act'
                                    ? `${item.name}`
                                    : `${item.type === "design" ? "设计值" : "运行数据"} ${item.id}`
                                }
                            </span>
                            <Checkbox
                                checked={selectedIds.includes(item.id)}
                                onChange={() => toggleSelect(item.id)}
                            />
                        </div>
                    ))
                )}
            </div>

            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#666" }}>已选：{selectedIds.length} 项</span>
                <Button type="primary" size="small" onClick={handleConfirm} loading={confirmLoading}>
                    {confirmLoading ? "正在对比..." : "确认选择"}
                </Button>
            </div>
        </div>
    );

    return (
        <div className="process-component-container">
            <div className="result-header-container" style={{ position: "relative" }}>
                <div className="result-header">
                    <span className="device-name-text">
                        {/* {elementDesc[selectTreeButton.name]
                            ? `${selectTreeButton.name} (${elementDesc[selectTreeButton.name]})`
                            : selectTreeButton.name} */}
                            {selectTreeButton.name}
                    </span>
                </div>

                <div className="result-page-list">
                    {elementTabs.map((item: string) => (
                        <button
                            key={item}
                            className={`result-tab-btn ${item === selectResultPage ? "active" : ""}`}
                            onClick={() => setSelectResultPage(item)}
                        >
                            {item}
                        </button>
                    ))}
                </div>
                <div style={{ position: "absolute", top: 0, right: 0, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14 }}>温度：</span>
                    <Select value={tempUnit} onChange={setTempUnit} style={{ width: 60 }} size="small"
                      options={[{ value: "℃", label: "℃" }, { value: "K", label: "K" }]} />
                    <span style={{ fontSize: 14 }}>压力：</span>
                    <Select value={pressUnit} onChange={setPressUnit} style={{ width: 80 }} size="small"
                      options={[{ value: "Pa", label: "Pa" }, { value: "bar", label: "bar" }, { value: "KPa", label: "KPa" }, { value: "MPa", label: "MPa" }, { value: "atm", label: "atm" }]} />
                    <Dropdown
                        overlay={dropdownContent}
                        trigger={["click"]}
                        open={dropdownOpen}
                        onOpenChange={handleDropdownOpen}
                        getPopupContainer={() => document.body}
                        overlayStyle={{ zIndex: 100002 }}
                        placement="bottomRight"
                    >
                        <Button type="primary">
                            对比设计值/运行数据 <DownOutlined />
                        </Button>
                    </Dropdown>
                </div>
            </div>
            <div style={{ fontSize: 14, color: "#666" }}>{elementDescMap?.[selectTreeButton.name] || ""}</div>

            <div className="element-page-content">{showElementPage()}</div>
        </div>
    );
};

export default ProcessComponent;